import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUpRight, Sparkles, ShieldCheck, Zap, GitBranch, Wallet,
  Slack, MessageCircle, TrendingUp, Crown, Lock, Check, Activity, Loader2, ArrowRight, Gauge, Clock,
} from "lucide-react";
import AnimatedConnexisMark from "@/components/marketing/AnimatedConnexisMark";
import "./Start.css";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

/**
 * /start — The Connexis Launch Partner conversion page.
 *
 * Design intent: premium B2B SaaS hero with animated routing network, live
 * scarcity counter, an interactive bonus calculator (the WOW moment),
 * step-by-step 60-second onboarding visual, and an unambiguous CTA.
 *
 * No freebie energy — this is "earn your seat" framing. Targets serious buyers
 * with £5,000+ minimum top-ups. Bonus is paid in wallet credit, never cash.
 */
export default function Start() {
  const rain = useCodeRain("start", "dark");
  const reduced = useReducedMotion();
  const [status, setStatus] = useState(null);
  const [slider, setSlider] = useState(15000);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.18], [1, 0]);
  const heroY       = useTransform(scrollYProgress, [0, 0.18], [0, -40]);

  useEffect(() => {
    api.get("/launch-partner/status")
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ active: true, seats_total: 50, seats_taken: 0, seats_left: 50, bonus_pct: 0.20, min_topup: 5000, closed: false }));
  }, []);

  const bonusPct = (status?.bonus_pct ?? 0.20);
  const minTopup = (status?.min_topup ?? 5000);
  const seatsLeft = status?.seats_left ?? 50;
  const seatsTotal = status?.seats_total ?? 50;
  const seatsTaken = status?.seats_taken ?? 0;
  const closed = status?.closed;

  const bonus = useMemo(() => Math.round(slider * bonusPct), [slider, bonusPct]);
  const total = slider + bonus;

  return (
    <div className="start-page bg-slate-950 text-white" data-testid="start-page">

      {/* ════════════════════════════════════════════════════════════════════
          HERO
          ════════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden border-b border-white/5 min-h-[88vh] flex items-center">
        <HeroBackground reduced={reduced} />

        <motion.div
          className="relative w-full max-w-6xl mx-auto px-6 py-20 lg:py-28"
          style={reduced ? {} : { opacity: heroOpacity, y: heroY }}
        >
          {/* Launch Partner ribbon */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-sm border border-white/10 bg-white/5 backdrop-blur-md text-[10.5px] uppercase tracking-[0.22em] text-white/75 font-mono mb-8"
            data-testid="start-launch-ribbon"
          >
            <span className="relative flex size-1.5">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
              <span className="relative size-1.5 rounded-full bg-emerald-400" />
            </span>
            Connexis Launch Partner Programme · 2026 cohort
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl tracking-tight font-semibold leading-[0.96] max-w-4xl"
          >
            Get a routing fabric.<br />
            <span className="text-white/55">Not another</span><br />
            <ShinyText>lead vendor.</ShinyText>
          </motion.h1>

          {/* Sub */}
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-8 text-[16px] sm:text-[17.5px] text-white/65 max-w-2xl leading-relaxed"
          >
            Join the first <strong className="text-white">{seatsTotal} acquisition teams</strong> operating production-grade lead infrastructure on Connexis. <br className="hidden md:block"/>
            Earn a <strong className="text-white">+{Math.round(bonusPct*100)}% wallet bonus</strong> on your first top-up, white-glove onboarding, and priority publisher routing for 30 days.
          </motion.p>

          {/* CTA + seat counter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <Button asChild className="rounded-full bg-white text-slate-950 hover:bg-white/90 font-semibold px-7 h-12 text-[15px] gap-2 group shadow-[0_8px_30px_-8px_rgba(255,255,255,0.4)]" data-testid="start-hero-cta">
              <Link to="/login?tab=signup&launch=1&role=client">
                Claim a launch partner seat
                <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Button>

            <SeatCounter taken={seatsTaken} total={seatsTotal} left={seatsLeft} closed={closed} />
          </motion.div>

          {/* Proof strip */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10 rounded-md overflow-hidden backdrop-blur-md"
          >
            {[
              { v: "98ms",    l: "median routing" },
              { v: "240k+",  l: "leads routed" },
              { v: "99.99%", l: "uptime SLA" },
              { v: "0",      l: "data breaches" },
            ].map((s) => (
              <div key={s.l} className="bg-slate-950/60 px-5 py-5">
                <div className="text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums">{s.v}</div>
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/45 font-mono mt-1.5">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-slate-950 pointer-events-none" />
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          OFFER + CALCULATOR
          ════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6">

          <SectionEyebrow text="The offer" />
          <h2 className="mt-4 text-4xl sm:text-5xl tracking-tight font-semibold leading-tight max-w-3xl">
            One programme.<br />
            <span className="text-white/55">Four asymmetric advantages.</span>
          </h2>

          <div className="mt-16 grid lg:grid-cols-5 gap-6">

            {/* Calculator (the WOW moment) */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-3 rounded-md border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-sm p-7 sm:p-9 relative overflow-hidden"
              data-testid="start-calculator"
            >
              <div className="absolute -top-32 -right-32 size-80 rounded-full bg-[#0055FF]/15 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 size-72 rounded-full bg-[#22D3A6]/10 blur-3xl pointer-events-none" />

              <div className="relative flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-mono">Bonus calculator</div>
                  <h3 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">See your launch bonus instantly</h3>
                </div>
                <div className="rounded-sm bg-white/5 border border-white/10 px-3 py-1.5">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 font-mono">First top-up</div>
                  <div className="text-sm font-semibold text-white">min £{minTopup.toLocaleString()}</div>
                </div>
              </div>

              {/* Slider */}
              <div className="mt-9 relative">
                <input
                  type="range"
                  min={5000}
                  max={50000}
                  step={500}
                  value={slider}
                  onChange={(e) => setSlider(parseInt(e.target.value, 10))}
                  className="w-full h-1.5 rounded-full appearance-none bg-white/10 outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(255,255,255,0.12)] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:size-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                  data-testid="start-slider"
                />
                <div className="mt-2.5 flex items-center justify-between text-[10.5px] uppercase tracking-[0.16em] text-white/40 font-mono">
                  <span>£5,000</span><span>£50,000</span>
                </div>
              </div>

              {/* Numbers — stacked on a phone so a six-figure total can never clip */}
              <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/10 rounded-md overflow-hidden">
                <Stat label="You top up" value={`£${slider.toLocaleString()}`} sub="from £5,000" />
                <Stat label={`+${Math.round(bonusPct*100)}% bonus`} value={`£${bonus.toLocaleString()}`} sub="wallet credit" accent />
                <Stat label="Wallet credit" value={`£${total.toLocaleString()}`} sub={`= ${ratioWording(slider, total)}`} highlight />
              </div>

              <p className="mt-7 text-[12.5px] text-white/45 leading-relaxed max-w-2xl">
                Bonus is credit, not cash — spendable only on real leads through Connexis. Applies once, on your first qualifying top-up. After that you're on standard wallet economics.
              </p>
            </motion.div>

            {/* Other advantages stack */}
            <div className="lg:col-span-2 space-y-3">
              {OFFER_PERKS.map((p, i) => (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="start-lift rounded-md border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04] hover:border-white/15"
                >
                  <div className="flex items-start gap-3">
                    <span className="inline-flex size-9 items-center justify-center rounded-sm bg-white/8 text-white shrink-0">
                      <p.Icon className="size-4" strokeWidth={1.5} />
                    </span>
                    <div>
                      <h4 className="text-[14.5px] font-semibold text-white tracking-tight leading-tight">{p.label}</h4>
                      <p className="mt-1.5 text-[12.5px] text-white/55 leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          60-SECOND ONBOARDING
          ════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <SectionEyebrow text="Onboarding" />
          <h2 className="mt-4 text-4xl sm:text-5xl tracking-tight font-semibold leading-tight max-w-3xl">
            Live in <span className="text-[#22D3A6]">60 seconds.</span><br />
            <span className="text-white/55">Not 60 days.</span>
          </h2>
          <p className="mt-5 text-[15px] text-white/55 max-w-2xl">
            From the moment you register to the moment your first lead lands in your wallet — measured in real time.
          </p>

          <div className="mt-14">
            <div className="grid lg:grid-cols-4 gap-4 relative">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative"
                >
                  <div className="rounded-md border border-white/10 bg-white/[0.02] p-6 start-lift hover:bg-white/[0.04] hover:border-white/15 h-full">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex size-9 items-center justify-center rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/30 text-white">
                        <s.Icon className="size-4" strokeWidth={1.6} />
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/35">step {i+1}</span>
                    </div>
                    <h3 className="mt-5 text-[15px] font-semibold tracking-tight">{s.title}</h3>
                    <p className="mt-2 text-[12.5px] text-white/55 leading-relaxed">{s.body}</p>
                    <div className="mt-5 pt-4 border-t border-white/8">
                      <div className="text-[10px] uppercase tracking-[0.16em] text-white/35 font-mono">duration</div>
                      <div className="text-sm font-semibold tabular-nums">{s.duration}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          WHO THIS IS FOR / NOT FOR (filter copy)
          ════════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-24 lg:py-32 border-b border-white/5">
        {rain.on && <CodeRain variant="dark" intensity={rain.intensity} />}
        <div className="relative max-w-6xl mx-auto px-6">
          <SectionEyebrow text="Honest fit" />
          <h2 className="mt-4 text-4xl sm:text-5xl tracking-tight font-semibold leading-tight max-w-3xl">
            Built for serious buyers.<br />
            <span className="text-white/55">Not curious tyre-kickers.</span>
          </h2>

          <div className="mt-14 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden">
            <div className="bg-slate-950 p-8 sm:p-10">
              <div className="flex items-center gap-2.5 mb-6">
                <span className="inline-flex size-8 items-center justify-center rounded-sm bg-[#22D3A6]/15 text-[#22D3A6] border border-[#22D3A6]/25">
                  <Check className="size-4" strokeWidth={2.5} />
                </span>
                <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-white">A fit if you</h3>
              </div>
              <ul className="space-y-4">
                {FIT.map((f, i) => (
                  <li key={i} className="flex gap-3 text-[14px] text-white/80 leading-relaxed">
                    <Check className="size-3.5 mt-1 text-[#22D3A6] shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-950 p-8 sm:p-10">
              <div className="flex items-center gap-2.5 mb-6">
                <span className="inline-flex size-8 items-center justify-center rounded-sm bg-white/5 text-white/55 border border-white/10">
                  <Lock className="size-4" strokeWidth={1.7} />
                </span>
                <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-white/55">Not a fit if you</h3>
              </div>
              <ul className="space-y-4">
                {NOT_FIT.map((f, i) => (
                  <li key={i} className="flex gap-3 text-[14px] text-white/55 leading-relaxed">
                    <span className="mt-1 size-1 rounded-full bg-white/30 shrink-0 mt-2.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          FINAL CTA
          ════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(0,85,255,0.18),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:64px_64px] pointer-events-none" />

        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex justify-center mb-2"
          >
            <AnimatedConnexisMark size={72} glow="#0055FF" />
          </motion.div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold leading-[1.02]">
            Seats are <ShinyText>not refilled.</ShinyText>
          </h2>
          <p className="mt-6 text-[16px] text-white/60 max-w-xl mx-auto leading-relaxed">
            Once the 50 launch partner seats are claimed, the +{Math.round(bonusPct*100)}% bonus, dedicated onboarding, and priority routing offer closes — not paused, closed.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild className="rounded-full bg-white text-slate-950 hover:bg-white/90 font-semibold px-7 h-12 text-[15px] gap-2 group shadow-[0_8px_30px_-8px_rgba(255,255,255,0.4)]" data-testid="start-bottom-cta">
              <Link to="/login?tab=signup&launch=1&role=client">
                Claim my seat now
                <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white h-12 px-6">
              <Link to="/book-demo" data-testid="start-bottom-demo">First, walk me through it</Link>
            </Button>
          </div>

          <div className="mt-10">
            <SeatCounter taken={seatsTaken} total={seatsTotal} left={seatsLeft} closed={closed} centred />
          </div>
        </div>
      </section>
    </div>
  );
}

/* ═══════════════════════ Sub-components ═══════════════════════ */

function HeroBackground({ reduced }) {
  // Pre-computed scattered node positions so the layout is stable but feels organic.
  const nodes = [
    { l: "8%",  t: "18%", s: 10 },
    { l: "16%", t: "62%", s: 8  },
    { l: "30%", t: "30%", s: 14 },
    { l: "44%", t: "78%", s: 9  },
    { l: "58%", t: "20%", s: 11 },
    { l: "70%", t: "55%", s: 14 },
    { l: "84%", t: "32%", s: 10 },
    { l: "92%", t: "72%", s: 9  },
  ];
  return (
    <>
      {/* Aurora glows */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="start-aurora absolute -top-40 -left-40 size-[40rem] rounded-full bg-[#0055FF]/25 blur-[120px]" />
        <div className="start-aurora absolute -bottom-32 -right-20 size-[36rem] rounded-full bg-[#22D3A6]/15 blur-[120px]" style={{ animationDelay: "-6s" }} />
      </div>
      {/* Grid */}
      <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />

      {/* Network nodes + pulses */}
      {!reduced && (
        <div className="absolute inset-0 hidden lg:block pointer-events-none" aria-hidden>
          {nodes.map((n, i) => (
            <React.Fragment key={i}>
              <div
                className="start-network-node"
                style={{ left: n.l, top: n.t, width: n.s, height: n.s, animationDelay: `${i * 0.4}s` }}
              />
              <div
                className="start-network-pulse"
                style={{ left: n.l, top: n.t, width: n.s, height: n.s, animationDelay: `${i * 0.5}s` }}
              />
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
}

function SeatCounter({ taken, total, left, closed, centred }) {
  const pct = Math.min(100, Math.round((taken / total) * 100));
  return (
    <div className={`inline-flex flex-col gap-2 ${centred ? "items-center" : "items-start"}`} data-testid="seat-counter">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex size-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
        <span className="text-[12px] text-white/65 font-medium">
          <span className="text-white tabular-nums font-semibold">{left}</span> of <span className="tabular-nums">{total}</span> launch partner seats remain
        </span>
      </div>
      <div className="w-64 h-1 rounded-full bg-white/8 overflow-hidden" aria-hidden>
        <div className="h-full bg-gradient-to-r from-[#0055FF] to-[#22D3A6] transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
      {closed && <div className="text-[11px] text-rose-300 font-mono uppercase tracking-[0.16em] mt-1">Programme closed</div>}
    </div>
  );
}

function SectionEyebrow({ text }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-white/10 bg-white/[0.03] text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-mono">
      <span className="size-1 rounded-full bg-[#22D3A6]" /> {text}
    </div>
  );
}

function Stat({ label, value, sub, accent, highlight }) {
  return (
    <div className={`px-5 py-6 ${highlight ? "bg-slate-950/40" : "bg-slate-950"}`}>
      <div className={`text-[10px] uppercase tracking-[0.16em] font-mono ${accent ? "text-[#22D3A6]" : "text-white/45"}`}>{label}</div>
      <div className={`mt-2 text-2xl sm:text-3xl font-semibold tabular-nums tracking-tight ${accent ? "text-[#22D3A6]" : highlight ? "text-white" : "text-white"}`}>{value}</div>
      <div className="text-[11px] text-white/45 mt-1.5">{sub}</div>
    </div>
  );
}

function ShinyText({ children }) {
  return (
    <span className="bg-gradient-to-r from-white via-[#22D3A6] to-[#0055FF] bg-clip-text text-transparent">
      {children}
    </span>
  );
}

function ratioWording(topup, total) {
  const factor = (total / topup).toFixed(2);
  return `×${factor} buying power`;
}

/* ═══════════════════════ Data ═══════════════════════ */

const OFFER_PERKS = [
  {
    Icon: Slack,
    label: "Dedicated onboarding for 30 days",
    body: "Direct Slack channel with Connexis ops, a named CSM, and your webhook integration built FOR you — not by you.",
  },
  {
    Icon: GitBranch,
    label: "Priority publisher routing",
    body: "Your campaign gets first dibs on the highest-quality leads in your sector for the first 30 days. Then standard CPL routing kicks in.",
  },
  {
    Icon: ShieldCheck,
    label: "No setup, integration, or platform fees",
    body: "Ever. You pay only for leads consumed. Wallet float is yours until spent — refundable on programme exit.",
  },
  {
    Icon: Crown,
    label: "Founding partner badge",
    body: "Listed on /customers (anonymised by default), eligible for joint case studies, and first refusal on new sectors before public launch.",
  },
];

const STEPS = [
  { Icon: Sparkles,    title: "Register",        body: "Email + company. No card needed. Launch partner seat reserved instantly.", duration: "~30s" },
  { Icon: GitBranch,   title: "Pick your sector", body: "Tell us which vertical. We provision a live campaign that begins routing the moment you fund.", duration: "~20s" },
  { Icon: Wallet,      title: "Top up £5,000+",    body: "Stripe or bank transfer. +20% bonus credit lands the same second the payment clears.", duration: "~60s" },
  { Icon: TrendingUp,  title: "Receive leads",   body: "First lead typically hits your webhook within minutes — depends on sector volume. Auto Follow-Up fires SMS+email.", duration: "Live" },
];

const FIT = [
  "You're running a UK call centre, broker, claims firm or insurance brand spending £5,000+/month on inbound leads.",
  "You have a CRM or webhook endpoint to receive leads — or want us to build the integration for you.",
  "You want infrastructure, not a lead list. CPL routing, business-hours control, AI follow-up, audit trail.",
  "You're FCA-aware (or your sector's equivalent compliance) and want consent + dispute trails built-in.",
];

const NOT_FIT = [
  "You want £100 of free leads. Connexis Launch Partner has no free credit — only a +20% bonus on real top-ups.",
  "You're a single agent running occasional campaigns under £5,000/month.",
  "You want raw lead lists (CSV exports without routing). We're not a vendor — we're infrastructure.",
  "You can't pass consent + audit trail compliance for the consumer data you're buying.",
];
